import { Icon, MuiAlert, Spin, strIsNullOrEmpty, Typography, useRenderer } from '@fox/atoms';
import { ResponsiveTable, ResponsiveTableColumn } from '@fox/data-grid';
import { FlexRowSpaced } from '@fox/layout';
import MUITooltip from '@mui/material/Tooltip';
import { useSubscribeTopicWithHandler } from 'fox-extensions';
import moment from 'moment';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { useTableSettingsContext } from '../../../table-settings-context';
import { FINANCIAL_DATA_FETCHED } from '../../../topics';
import { usePlans } from '../../query-hooks/query-implementations';
import { PlanOptionType, SubscriptionStatus, SwiftFoxSubscription } from '../contracts';
import { TableWrapper } from '../receipts';
import { FeeSummary, SubscriptionAutoRenewStatusChip, SubscriptionStatusChip, formatCurrency, renderDate } from '../shared';
import { EditSubscriptionDesktop, EditSubscriptionMobile } from './edit-subscription';
import { RenewSubscriptionDeskTop, RenewSubscriptionMobile } from './renew-subscription';

export function SubscriptionsTable({
	useLinkedOrganisationData,
	rowsExpandedOnSmallScreens,
	allowedPlanCategories,
	allowedProviders
}: {
	useLinkedOrganisationData: boolean;
	rowsExpandedOnSmallScreens: boolean;
	allowedProviders?: string[];
	allowedPlanCategories?: string[];
}) {
	const { contentId, requestToken } = useContentContext();
	const { error: tableSettingsError, getTableSettings, loading: tableSettingsLoading } = useTableSettingsContext();
	const {
		getSubscriptions,
		subscriptionsError,
		subscriptionsLoading,
		orgSubscriptionsLoading,
		orgSubscriptionsError,
		getAllPayableLineItems,
		payableLineItemsLoading,
		payableLineItemsError,
		orgPayableLineItemsError,
		orgPayableLineItemsLoading
	} = useFinancialContext();
	const subscriptions = getSubscriptions(useLinkedOrganisationData, allowedPlanCategories);
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	const { error: plansError, data: plansResult, loading: plansLoading } = usePlans({ requestToken, contentId });

	const errors = [];
	if (!strIsNullOrEmpty(subscriptionsError)) errors.push(subscriptionsError);
	if (!strIsNullOrEmpty(orgSubscriptionsError) && useLinkedOrganisationData) errors.push(orgSubscriptionsError);
	if (!strIsNullOrEmpty(plansError)) errors.push(plansError);
	if (!strIsNullOrEmpty(tableSettingsError)) errors.push(tableSettingsError);
	if (!strIsNullOrEmpty(payableLineItemsError)) errors.push(payableLineItemsError);
	if (!strIsNullOrEmpty(orgPayableLineItemsError)) errors.push(orgPayableLineItemsError);

	if (errors.length)
		return (
			<MuiAlert severity="error">
				{errors.map((e, i) => {
					return <Typography key={i}>{e}</Typography>;
				})}
			</MuiAlert>
		);

	const tableSettings = getTableSettings('Subscriptions');
	const allPayableLineItems = getAllPayableLineItems(useLinkedOrganisationData, allowedPlanCategories);
	if (!tableSettings) return <></>;

	const columns: ResponsiveTableColumn<SwiftFoxSubscription>[] = [
		{
			title: 'Plan',
			dataIndex: 'fee.plan.name',
			render: record => record.fee.plan.name,
			showOnMobileRow: true
		},
		{
			title: 'Plan cost',
			dataIndex: 'fee.fee',
			render: record => <FeeSummary selectedFee={record.fee} nominatedFee={record.nominatedFee} />
		},
		{
			title: 'Paid to',
			dataIndex: 'paidTo',
			render: record => {
				return (
					<FlexRowSpaced>
						{renderDate(record.paidTo)}
						{record.status === SubscriptionStatus.Active && moment(record.paidTo).isBefore(moment()) ? (
							<MUITooltip title={<>Payment is now due</>}>
								<div>
									<Icon iconName="Warning" />
								</div>
							</MUITooltip>
						) : null}
					</FlexRowSpaced>
				);
			}
		},
		{
			title: 'Arrears',
			dataIndex: 'arrears',
			render: record => {
				return formatCurrency(record.arrears, record.fee.currency);
			}
		},
		{
			title: 'Options',
			dataIndex: 'options',
			render: record => (
				<span>
					{record.options?.length
						? record.options
								.filter(a => !a.limit || a.uses < a.limit)
								.map(
									a =>
										`${a.name} (${formatCurrency(a.amount * (a.type === PlanOptionType.DISCOUNT ? -1 : 1))}${
											a.limit > 0 && a.uses < a.limit ? `, ${a.limit - a.uses} Remaining` : ''
										})`
								)
								.join(', ')
						: '-'}
				</span>
			)
		},
		{
			title: 'Auto renew',
			dataIndex: 'autoRenew',
			render: record => <SubscriptionAutoRenewStatusChip autoRenew={record.autoRenew} />
		},
		{
			title: 'Auto renew date',
			dataIndex: 'nextAttempt',
			render: record => (record.autoRenew ? renderDate(record.nextAttempt) : '-')
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: record => <SubscriptionStatusChip status={record.status} />
		},
		{ title: 'Actions', isActionColumn: true }
	];

	const hasPayableLineItems = allPayableLineItems.some(p => p.payableLineItems.length);
	if (hasPayableLineItems)
		columns.splice(1, 0, {
			title: 'Renew',
			dataIndex: 'renew',
			hideOnMobile: true,
			render: record => (
				<RenewSubscriptionDeskTop
					record={record}
					plans={plansResult}
					allowedProviders={allowedProviders}
					useLinkedOrganisationData={useLinkedOrganisationData}
				/>
			)
		});

	const columnsToUse = columns.filter(c => {
		const columnSettings = tableSettings.columnSettings.find(s => s.dataIndex === c.dataIndex);
		if (c.isActionColumn || !columnSettings) {
			return true;
		} else {
			return columnSettings.isVisible;
		}
	});

	return (
		<TableWrapper>
			<Spin
				spinning={
					plansLoading ||
					subscriptionsLoading ||
					orgSubscriptionsLoading ||
					payableLineItemsLoading ||
					tableSettingsLoading ||
					orgPayableLineItemsLoading
				}
			>
				{subscriptions?.length === 0 || !plansResult ? (
					<MuiAlert severity="info">No subscription data to display</MuiAlert>
				) : (
					<ResponsiveTable<SwiftFoxSubscription>
						data={subscriptions ?? []}
						rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
						rowActionComponent={record => (
							<FlexRowSpaced>
								<EditSubscriptionDesktop
									record={record}
									plans={plansResult}
									allowedPlanCategories={allowedPlanCategories}
									allowedProviders={allowedProviders}
									useLinkedOrganisationData={useLinkedOrganisationData}
								/>
							</FlexRowSpaced>
						)}
						mobileRowActionComponent={record => (
							<FlexRowSpaced>
								<EditSubscriptionMobile
									record={record}
									plans={plansResult}
									allowedPlanCategories={allowedPlanCategories}
									allowedProviders={allowedProviders}
									useLinkedOrganisationData={useLinkedOrganisationData}
								/>
								<RenewSubscriptionMobile
									record={record}
									plans={plansResult}
									allowedProviders={allowedProviders}
									useLinkedOrganisationData={useLinkedOrganisationData}
								/>
							</FlexRowSpaced>
						)}
						columns={columnsToUse}
					/>
				)}
			</Spin>
		</TableWrapper>
	);
}
